import type { ReviewItem } from "../types/reviewItem";

/**
 * Fila expandible con lo OPCIONAL de una factura: el comentario del Aprobador
 * y el comprobante adicional. [NUEVO 08/09/2026]
 *
 * Por qué existe: la celda de Comentario vivía siempre abierta en la tabla
 * (una textarea de 44px de alto mínimo + su botón, ~85px en CADA fila), y era
 * lo que más estiraba la fila del Paso 2 — medido, ~146px por fila, o sea ~4
 * filas por pantalla. Encima el adjunto necesitaba lugar para un input de
 * archivo y el nombre del archivo, que en una celda de tabla no entra.
 *
 * El criterio que ordena las dos cosas:
 *   - lo que es OBLIGATORIO para autorizar queda inline en la tabla
 *     (Carpeta, Fecha de llegada, Proyecto),
 *   - lo que es OPCIONAL y nunca bloquea vive acá, a un click.
 *
 * Se reusa el mismo mecanismo de fila expandible que `LineasRow` (un `<tr>`
 * con colSpan), que ya estaba probado. Las dos filas pueden estar abiertas a
 * la vez: son `<tr>` independientes con estados independientes.
 *
 * Es un componente presentacional: no llama a la API ni valida nada. La página
 * es la que valida, sube y muestra los errores por su `useAccion` — igual que
 * el resto de las acciones de escritura.
 */
interface Props {
  item: ReviewItem;
  colSpan: number;
  /** Solo el Paso 2 tiene comentario; en Contaduría se omite. */
  comentario?: {
    valor: string;
    onChange: (valor: string) => void;
    onGuardar: () => void;
  };
  /** false = el adjunto se puede VER pero no subir ni reemplazar (la factura
   *  está en el paso del otro rol, o se está mirando en modo seguimiento). */
  puedeAdjuntar: boolean;
  onElegirArchivo: (file: File) => void;
  onVerAdjunto: () => void;
  enCurso: boolean;
}

export function NotasAdjuntoRow({
  item,
  colSpan,
  comentario,
  puedeAdjuntar,
  onElegirArchivo,
  onVerAdjunto,
  enCurso,
}: Props) {
  return (
    <tr className="notas-row">
      <td colSpan={colSpan}>
        <div className="notas-grid">
          {comentario && (
            <div className="notas-bloque">
              <label className="notas-label" htmlFor={`comentario-${item.id}`}>
                Comentario (opcional)
              </label>
              <textarea
                id={`comentario-${item.id}`}
                placeholder="Observación para dejar registrada..."
                value={comentario.valor}
                onChange={(e) => comentario.onChange(e.target.value)}
              />
              <button
                type="button"
                className="btn-ghost"
                disabled={enCurso}
                onClick={comentario.onGuardar}
              >
                Guardar comentario
              </button>
            </div>
          )}

          <div className="notas-bloque">
            <span className="notas-label">Comprobante adicional (opcional)</span>

            {item.adjunto_blob_url ? (
              <div className="notas-adjunto-actual">
                <button type="button" className="btn-ghost" onClick={onVerAdjunto}>
                  Ver adjunto
                </button>
                <span className="notas-nombre" title={item.adjunto_nombre_original ?? undefined}>
                  {item.adjunto_nombre_original ?? "archivo adjunto"}
                </span>
              </div>
            ) : (
              <span className="subtitle">Sin comprobante adjunto.</span>
            )}

            {/* El input NO se esconde cuando ya hay un adjunto: hay un solo
                casillero por factura y subir otro lo reemplaza, así que
                reemplazar tiene que seguir siendo posible. */}
            {puedeAdjuntar && (
              <>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  disabled={enCurso}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    // Se limpia el input para que elegir el MISMO archivo de
                    // nuevo (después de un error) vuelva a disparar onChange.
                    e.target.value = "";
                    if (file) onElegirArchivo(file);
                  }}
                />
                <span className="subtitle">
                  PDF, JPG o PNG, hasta 10 MB.
                  {item.adjunto_blob_url ? " Subir otro reemplaza al actual." : ""}
                </span>
              </>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

/** Toggle de la fila, con el mismo vocabulario visual que `LineasToggle`.
 *  Marca con un punto cuando ya hay un adjunto, para que se vea desde la
 *  tabla sin tener que abrir cada fila. */
export function NotasToggle({
  expanded,
  tieneAdjunto,
  onToggle,
}: {
  expanded: boolean;
  tieneAdjunto: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className="btn-ghost"
      title={tieneAdjunto ? "Tiene un comprobante adjunto" : "Comentario y comprobante adicional"}
      onClick={onToggle}
    >
      {expanded ? "▾" : "▸"} Notas{tieneAdjunto ? " ●" : ""}
    </button>
  );
}
